import { isEmpty } from 'lodash';
import { CSSProperties, useCallback, useMemo } from 'react';
import { View } from '@shared/components/View';
import { FileUpload } from '@shared/components/blocks/types';
import { MBLoadingIndicator } from '@shared/components/loading';
import { FileField } from '@shared/data/entity/fields/FileField';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { UpdateField } from '@shared/scenes/entity/types';
import {
  getComponentIcon,
  getComponentLabel,
  getComponentPlaceholder,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { AddMoreButton } from '../AddMoreButton';
import { EditFieldContainer } from '../EditFieldContainer';
import { FileInput } from './FileInput';
import { useFileUpload } from './useFileUpload';

interface Props {
  field: FileField | null;
  component: API.FileComponent;
  updateField: UpdateField;
  error?: string;
  style?: CSSProperties;
}

const emptyAcceptedContentTypes = [];

export function FileEditField({ field, component, updateField, error, style }: Props) {
  const responsiveForms = useResponsiveFormContext();
  const files = useMemo(() => field?.files ?? [], [field?.files]);
  const isReadOnly = component.readonly ?? false;
  const hasFiles = !isEmpty(files);
  const acceptedContentTypes = component.acceptedContentTypes ?? emptyAcceptedContentTypes;

  const onFileAdded = useCallback(
    (addedFiles: FileUpload[]) => {
      updateField(component.id, prevField => {
        const prevFiles = (prevField as FileField)?.files ?? [];
        const updatedFiles = [...prevFiles, ...addedFiles];
        return new FileField(updatedFiles);
      });
    },
    [component.id, updateField],
  );

  const onFileRemoved = useCallback(
    (removedFile: FileUpload) => {
      updateField(component.id, prevField => {
        const prevFiles = (prevField as FileField)?.files ?? [];
        const updatedFiles = prevFiles.filter(file => file.url !== removedFile.url);
        return new FileField(updatedFiles);
      });
    },
    [component.id, updateField],
  );

  const { handleDrop, showFilePicker, uploading } = useFileUpload({
    onFileAdded,
    onFileRemoved,
    acceptedContentTypes,
  });

  const icon = getComponentIcon(component);
  const label = getComponentLabel(component);
  const placeholder = getComponentPlaceholder(component);
  const showAddMoreFooter = responsiveForms && hasFiles && !isReadOnly;
  const printContent = files.map(f => f.name).join(', ') || undefined;
  const showLoadingFooter = responsiveForms && hasFiles && uploading;
  const showFooter = showAddMoreFooter || showLoadingFooter;

  return (
    <EditFieldContainer
      component={component}
      label={hasFiles ? `${label} (${files.length})` : label}
      showLabel={true}
      indentLabel={true}
      showIcon={false}
      error={error}
      style={style}
      largerInput={false}
      printContent={printContent}
      footer={
        showFooter && (
          <FileEditFieldFooter
            component={component}
            label={placeholder}
            showFilePicker={showFilePicker}
            showLoadingFooter={showLoadingFooter}
            showAddMoreFooter={showAddMoreFooter}
          />
        )
      }
    >
      <View
        marginHorizontal={responsiveForms ? undefined : -1}
        marginVertical={responsiveForms ? undefined : -0.5}
      >
        <FileInput
          files={files}
          icon={icon}
          placeholder={placeholder}
          acceptedContentTypes={acceptedContentTypes}
          onFileAdded={onFileAdded}
          onFileRemoved={onFileRemoved}
          isReadOnly={isReadOnly}
          handleDrop={handleDrop}
          showFilePicker={showFilePicker}
          uploading={uploading}
        />
      </View>
    </EditFieldContainer>
  );
}

interface FileEditFieldFooterProps {
  component: API.FileComponent;
  label?: string;
  showFilePicker: VoidFunction;
  showLoadingFooter: boolean;
  showAddMoreFooter: boolean;
}

function FileEditFieldFooter({
  component,
  label,
  showFilePicker,
  showLoadingFooter,
  showAddMoreFooter,
}: FileEditFieldFooterProps) {
  if (showLoadingFooter) {
    return (
      <View row paddingVertical={1}>
        <MBLoadingIndicator />
      </View>
    );
  }

  if (showAddMoreFooter) {
    return <AddMoreButton component={component} label={label} onPress={showFilePicker} />;
  }

  return null;
}
